import type { JSX } from 'react';
import { Spinner } from '@heroui/react';

import { usePatternExamples } from '~/hooks/useDesignPatterns';

import { Accordion } from '~/pages/Documentation/components/ui/Accordion';

import type { PatternDomain } from '~/types';

import { PatternContent } from './PatternContent';

const STACK_LABELS: Record<string, string> = {
  'python-fastapi': 'Python / FastAPI',
  'typescript-react': 'TypeScript / React',
  'google-adk': 'Google ADK',
  fastmcp: 'FastMCP',
  playwright: 'Playwright',
  alembic: 'Alembic',
  docker: 'Docker',
  gcp: 'GCP',
};

interface PatternExamplesProps {
  patternId: string;
  exampleStacks: string[];
  domain: PatternDomain;
}

function ExampleAccordionItem({
  domain,
  patternId,
  techStack,
}: {
  domain: PatternDomain;
  patternId: string;
  techStack: string;
}): JSX.Element {
  const { error, examples, isLoading } = usePatternExamples(patternId, techStack);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="md" />
      </div>
    );
  }

  if (error) {
    return <p className="text-error text-sm">Failed to load examples.</p>;
  }

  if (examples) {
    return <PatternContent content={examples.content} domain={domain} />;
  }

  return <></>;
}

function formatStackLabel(slug: string): string {
  return (
    STACK_LABELS[slug] ??
    slug
      .split('-')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ')
  );
}

export function PatternExamples({
  domain,
  exampleStacks,
  patternId,
}: PatternExamplesProps): JSX.Element {
  return (
    <div>
      {exampleStacks.map(stack => (
        <Accordion key={stack} title={formatStackLabel(stack)}>
          <ExampleAccordionItem patternId={patternId} techStack={stack} domain={domain} />
        </Accordion>
      ))}
    </div>
  );
}
